import { useInsightTypeLabel } from '@plato/app-shared/hooks/use-insight-type-label';
import { SrCustomerStatus, SrInsightType } from '@plato/data/starrocks/types';
import type { QuickFilter } from '@plato/ui-legacy/components/filters/lib/filter-utils';
import { mergeFilterOptions } from '@plato/ui-legacy/components/filters/lib/merge-filter-options';
import type { VerticalFilterOption } from '@plato/ui-legacy/components/filters/types/filter-types';
import { keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetManyCustomersFilters } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { getUsedFilterEventPayload } from '../../analytics/utils/filter-analytics.ts';
import { VerticalFilterTemplate } from '../../filters/templates/vertical-filter-template.tsx';
import {
    customersExpansionPotentialFilter,
    customersRevenueDeclineFilter,
} from '../constants/customer-list-filters.ts';
import { useCustomerListFilters } from '../hooks/use-customer-list-filters.tsx';

// Customer lifecycle order, not alphabet: a rep reads this list as a funnel from won to lost.
const statusSortOrder = [
    SrCustomerStatus.NEW,
    SrCustomerStatus.ACTIVE,
    SrCustomerStatus.AT_RISK,
    SrCustomerStatus.DORMANT,
    SrCustomerStatus.INACTIVE,
    SrCustomerStatus.BLOCKED,
];

// Ordered by how actionable the recommendation is, most urgent first.
const insightTypeSortOrder = [
    SrInsightType.CHURN_WARNING,
    SrInsightType.CROSS_SELL,
    SrInsightType.SHARE_OF_WALLET,
    SrInsightType.INDUSTRY_TOP,
    SrInsightType.PREVIOUSLY_PURCHASED,
    SrInsightType.FIRST_PURCHASE,
    SrInsightType.GLOBAL_TOP,
];

type ModalEditFilters = GetManyCustomersFilters;

type Props = {
    canGetFilters?: boolean;
};

export function CustomerListFilters({ canGetFilters }: Props) {
    const { t } = useTranslation();
    const getInsightTypeLabel = useInsightTypeLabel();
    const sendEvent = useAnalytics();

    const { appliedFilters, setAppliedFilters, defaultFilters } = useCustomerListFilters();

    const quickFilters: QuickFilter<GetManyCustomersFilters>[] = useMemo(
        () => [
            {
                id: 'my-expansion-potential',
                label: t('quick_filters__customers_my_plus_customers', { ns: 'filters' }),
                showByDefault: true,
                filter: customersExpansionPotentialFilter,
            },
            {
                id: 'my-revenue-decline',
                label: t('quick_filters__customers_my_minus_customers', { ns: 'filters' }),
                showByDefault: true,
                filter: customersRevenueDeclineFilter,
            },
        ],
        [t],
    );

    //-----------------------------------------------------------------------------------------------
    // Modal editing state
    //-----------------------------------------------------------------------------------------------

    const [modalEditFilters, setModalEditFilters] = useState<ModalEditFilters>(appliedFilters);

    const { data: filterOptions, isLoading: isLoadingFilterOptions } =
        api.platoUser.getManyCustomers.getFilterOptions.useQuery(modalEditFilters, {
            enabled: canGetFilters,
            placeholderData: keepPreviousData,
        });

    const { data: filterOptionsWithoutSearch } = api.platoUser.getManyCustomers.getFilterOptions.useQuery(
        { ...modalEditFilters, searchString: '' },
        {
            enabled: canGetFilters,
            placeholderData: keepPreviousData,
        },
    );

    const { data: editingModalCount, isLoading: isLoadingCount } =
        api.platoUser.getManyCustomers.getCount.useQuery(modalEditFilters);

    const getStatusLabel = (status: string): string => {
        switch (status) {
            case SrCustomerStatus.NEW:
                return t('status_new', { ns: 'customers-list', defaultValue: status });
            case SrCustomerStatus.ACTIVE:
                return t('status_active', { ns: 'customers-list', defaultValue: status });
            case SrCustomerStatus.AT_RISK:
                return t('status_at_risk', { ns: 'customers-list', defaultValue: status });
            case SrCustomerStatus.DORMANT:
                return t('status_dormant', { ns: 'customers-list', defaultValue: status });
            case SrCustomerStatus.INACTIVE:
                return t('status_inactive', { ns: 'customers-list', defaultValue: status });
            case SrCustomerStatus.BLOCKED:
                return t('status_blocked', { ns: 'customers-list', defaultValue: status });
            default:
                return status;
        }
    };

    // Ordered by how many distinct users actually apply each filter (PostHog, 180d), with the
    // geography filters (city / zip / region) and the customer attributes kept adjacent.
    const filters: VerticalFilterOption<GetManyCustomersFilters>[] = [
        {
            id: 'insightTypes' as const,
            label: t('filter_menu_title__insights', { ns: 'customers-list' }),
            type: 'checkbox',
            options: mergeFilterOptions(
                // Only surface insight types that have a UI label mapping; unmapped
                // types (e.g. ChurnWarningV2WebCategory) are hidden rather than shown raw.
                filterOptions?.insightTypes.filter((o) => getInsightTypeLabel(o.type) !== undefined),
                filterOptionsWithoutSearch?.insightTypes.filter(
                    (o) => getInsightTypeLabel(o.type) !== undefined,
                ),
                (o) => ({
                    id: o.type,
                    label: getInsightTypeLabel(o.type) ?? o.type,
                    value: o.type,
                    count: o.count,
                }),
                appliedFilters.insightTypes,
                { sortOrder: insightTypeSortOrder },
            ),
        },
        {
            id: 'salesRepIds' as const,
            label: t('filter_menu_title__sales_rep', { ns: 'customers-list' }),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.salesReps,
                filterOptionsWithoutSearch?.salesReps,
                (o) => ({
                    id: o.rep_id,
                    label: `${o.rep_id}: ${o.name}`,
                    value: o.rep_id,
                    count: o.count,
                }),
                appliedFilters.salesRepIds,
            ),
        },
        {
            id: 'statuses' as const,
            label: t('filter_menu_title__status', { ns: 'customers-list' }),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.statuses,
                filterOptionsWithoutSearch?.statuses,
                (o) => ({
                    id: o.status,
                    label: getStatusLabel(o.status),
                    value: o.status,
                    count: o.count,
                }),
                appliedFilters.statuses,
                { sortOrder: statusSortOrder },
            ),
        },
        {
            id: 'cityNames' as const,
            label: t('filter_menu_title__city', { ns: 'customers-list' }),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.cities,
                filterOptionsWithoutSearch?.cities,
                (o) => ({ id: o.city, label: o.city, value: o.city, count: o.count }),
                appliedFilters.cityNames,
            ),
        },
        {
            id: 'zipCodes' as const,
            label: t('filter_menu_title__zip_code', { ns: 'customers-list' }),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.zipCodes,
                filterOptionsWithoutSearch?.zipCodes,
                (o) => ({ id: o.zip_code, label: o.zip_code, value: o.zip_code, count: o.count }),
                appliedFilters.zipCodes,
            ),
        },
        {
            id: 'salesRegions' as const,
            label: t('filter_menu_title__sales_region', { ns: 'customers-list' }),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.salesRegions,
                filterOptionsWithoutSearch?.salesRegions,
                (o) => ({ id: o.sales_region, label: o.sales_region, value: o.sales_region, count: o.count }),
                appliedFilters.salesRegions,
            ),
        },
        {
            id: 'customerIds' as const,
            label: t('filter_menu_title__customer', { ns: 'customers-list' }),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.customers,
                filterOptionsWithoutSearch?.customers,
                (o) => ({ id: o.customer_id, label: o.customer_name ?? o.customer_id, value: o.customer_id }),
                appliedFilters.customerIds,
            ),
        },
        {
            id: 'customerClasses' as const,
            label: t('filter_menu_title__customer_class', { ns: 'customers-list' }),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.customerClasses,
                filterOptionsWithoutSearch?.customerClasses,
                (o) => ({
                    id: o.customer_class,
                    label: o.customer_class,
                    value: o.customer_class,
                    count: o.count,
                }),
                appliedFilters.customerClasses,
            ),
        },
        {
            id: 'industryNames' as const,
            label: t('filter_menu_title__industry', { ns: 'customers-list' }),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.industrys,
                filterOptionsWithoutSearch?.industrys,
                (o) => ({ id: o.industry, label: o.industry, value: o.industry, count: o.count }),
                appliedFilters.industryNames,
            ),
        },
        {
            id: 'companyIds' as const,
            label: t('filter_menu_title__company', { ns: 'customers-list' }),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.companies,
                filterOptionsWithoutSearch?.companies,
                (o) => ({
                    id: o.company_id,
                    label: o.company_name ?? o.company_id,
                    value: o.company_id,
                    count: o.count,
                }),
                appliedFilters.companyIds,
            ),
        },
    ];

    const handleApplyFilters = (filters: GetManyCustomersFilters) => {
        setAppliedFilters(filters);

        sendEvent('customer_list_filter_used', getUsedFilterEventPayload(filters));
    };

    return (
        <VerticalFilterTemplate<GetManyCustomersFilters>
            aclSubject="Customer"
            viewKey="customers.list"
            searchFilterKey="searchString"
            searchPlaceholder={t('customer_search_placeholder', { ns: 'customers-list' })}
            filters={filters}
            itemCount={editingModalCount ?? 0}
            isLoading={isLoadingFilterOptions || isLoadingCount}
            quickFilters={quickFilters}
            onFiltersChange={setModalEditFilters}
            defaultFilters={defaultFilters}
            appliedFilters={appliedFilters}
            onApplyFilters={handleApplyFilters}
        />
    );
}
